import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Circle, GoogleMap, Marker, OverlayView } from "@react-google-maps/api";

import MAP_STYLE from "../../home/components/mapStyle";
import { boundsForRadius, regionKey } from "../../../lib/map/mapRegion";
import { buildPinIcon } from "./mapMarkerIcon";

const MIN_CIRCLE_METERS = 1000;

const PIN_COLOR_SELECTED = "#AE6F28";
const PIN_COLOR_AVAILABLE = "#FFFFFF";
const PIN_COLOR_UNAVAILABLE = "#9A9A9A";

const FIT_PADDING = 24;

const CONTAINER_STYLE = { width: "100%", height: "100%" };

const MAP_OPTIONS = {
  styles: MAP_STYLE,
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: "cooperative",
};

const CIRCLE_OPTIONS = {
  fillColor: "#AE6F28",
  fillOpacity: 0.14,
  strokeColor: "#AE6F28",
  strokeOpacity: 0.35,
  strokeWeight: 1,
  clickable: false,
};

const ProductMapCard = ({ coordinates, suppliers, selectedId, onSelect }) => {
  const mapRef = useRef(null);
  const cardRef = useRef(null);
  const appliedRef = useRef("");

  const mappable = useMemo(
    () => (suppliers || []).filter((card) => card.hasCoordinates),
    [suppliers]
  );

  const focused = useMemo(
    () => mappable.find((card) => card.supplierId === selectedId) || null,
    [mappable, selectedId]
  );

  const radiusMeters = useMemo(() => {
    if (focused && Number.isFinite(focused.distanceKm)) {
      return Math.max(focused.distanceKm * 1000, MIN_CIRCLE_METERS);
    }

    const farthest = mappable.reduce(
      (max, card) =>
        Number.isFinite(card.distanceKm) ? Math.max(max, card.distanceKm) : max,
      0
    );

    return Math.max(farthest * 1000, MIN_CIRCLE_METERS);
  }, [focused, mappable]);

  const centre = useMemo(
    () =>
      coordinates
        ? { lat: coordinates.latitude, lng: coordinates.longitude }
        : null,
    [coordinates]
  );

  const applyRegion = useCallback(() => {
    const map = mapRef.current;
    const maps = window.google?.maps;

    if (!map || !maps || !coordinates) return;

    const key = regionKey(coordinates, radiusMeters, "detail");
    if (appliedRef.current === key) return;

    const bounds = boundsForRadius({
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      radiusMeters,
    });

    map.fitBounds(
      new maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east }
      ),
      FIT_PADDING
    );

    appliedRef.current = key;
  }, [coordinates, radiusMeters]);

  useEffect(() => {
    applyRegion();
  }, [applyRegion]);

  useEffect(() => {
    const node = cardRef.current;
    if (!node || typeof ResizeObserver !== "function") return undefined;

    const observer = new ResizeObserver(() => {
      appliedRef.current = "";
      applyRegion();
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [applyRegion]);

  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;
      applyRegion();
    },
    [applyRegion]
  );

  if (!centre) {
    return (
      <div className="sy-map-card sy-map-card-empty">
        <p>Turn on location to see how far each supplier is from you.</p>
      </div>
    );
  }

  return (
    <div className="sy-map-card" ref={cardRef}>
      <GoogleMap
        center={centre}
        zoom={13}
        options={MAP_OPTIONS}
        mapContainerStyle={CONTAINER_STYLE}
        onLoad={onLoad}
        onUnmount={() => {
          mapRef.current = null;
        }}
      >
        <Circle center={centre} radius={radiusMeters} options={CIRCLE_OPTIONS} />

        <OverlayView
          position={centre}
          mapPaneName={OverlayView.OVERLAY_LAYER}
          getPixelPositionOffset={(width, height) => ({
            x: -(width / 2),
            y: -(height / 2),
          })}
        >
          <span className="home-map-user-dot" />
        </OverlayView>

        {mappable.map((card) => {
          const isSelected = card.supplierId === selectedId;

          return (
            <Marker
              key={card.key}
              position={{ lat: card.latitude, lng: card.longitude }}
              title={card.name}
              onClick={() => onSelect(card)}
              zIndex={isSelected ? 2 : 1}
              icon={buildPinIcon(
                isSelected
                  ? PIN_COLOR_SELECTED
                  : card.deliverable
                  ? PIN_COLOR_AVAILABLE
                  : PIN_COLOR_UNAVAILABLE
              )}
            />
          );
        })}
      </GoogleMap>
    </div>
  );
};

export default React.memo(ProductMapCard);
